/**
 * Display and layout config for the home dashboard.
 */

import type { ElementWidth } from '@/types/interface-elements.types';
import type { DashboardSectionType } from '@/types/dashboard.types';

/** Catalog row for a dashboard widget (static title or a role plural). */
export interface DashboardSectionCatalogItem {
  id: string;
  type: DashboardSectionType;
  order: number;
  title?: string;
  roleCode?: string;
}

/** User-preference keys: remote dashboards store layout separately from in-person. */
export const DASHBOARD_PREFERENCE_KEY = 'dashboard';
/** Preference key for the remote-mode dashboard layout. */
export const DASHBOARD_REMOTE_PREFERENCE_KEY = 'dashboard-remote';

/** 12-column wrap grid used by dashboard rows. */
export const DASHBOARD_GRID_COLUMNS = 12;
/** Max widgets that can share one wrap row. */
export const DASHBOARD_MAX_SECTIONS_PER_ROW = 3;
/** Fallback height for the source-row hole while a lone section is dragged. */
export const DASHBOARD_DEFAULT_SOURCE_SECTION_HEIGHT = 300;

/** Horizontal cursor offset for the custom drag overlay. */
export const DASHBOARD_OVERLAY_OFFSET_X = 12;
/** Vertical cursor offset for the custom drag overlay. */
export const DASHBOARD_OVERLAY_OFFSET_Y = 12;

/** Droppable id for the hole left when a lone section is dragged. */
export const SOURCE_ROW_PLACEHOLDER_ID = 'source-row-placeholder';
/** Prefix for between-row drop zones (`drop-zone-after-row-${idx}`). */
export const DROP_ZONE_AFTER_ROW_PREFIX = 'drop-zone-after-row-';
/** Prefix for in-row droppables (`row-droppable-${idx}`). */
export const ROW_DROPPABLE_PREFIX = 'row-droppable-';

/** Widgets that have no sidebar menu and stay on the dashboard. */
export const ALWAYS_VISIBLE_DASHBOARD_SECTION_TYPES: readonly DashboardSectionType[] = [
  'stats',
  'postits',
  'sessions-chart',
  'resources-chart',
];

/** Default catalog: order / type / title (role titles resolved in utils). */
export const DASHBOARD_SECTION_CATALOG: DashboardSectionCatalogItem[] = [
  { id: 'section-stats', type: 'stats', order: 0, title: 'Statistiques' },
  { id: 'section-postits', type: 'postits', order: 1, title: 'Post-its' },
  { id: 'section-sessions', type: 'sessions', order: 2, title: 'Séances' },
  { id: 'section-pairs', type: 'pairs', order: 3, title: 'Binômes' },
  { id: 'section-calendar', type: 'calendar', order: 4, title: 'Calendrier' },
  { id: 'section-sessions-chart', type: 'sessions-chart', order: 5, title: 'Graphique Séances' },
  {
    id: 'section-resources-chart',
    type: 'resources-chart',
    order: 6,
    title: 'Graphique Ressources',
  },
  { id: 'section-establishments', type: 'establishments', order: 8, title: 'Établissements' },
  { id: 'section-tutors', type: 'tutors', order: 9, roleCode: 'tutor' },
  { id: 'section-students', type: 'students', order: 10, roleCode: 'student' },
  { id: 'section-volunteers', type: 'volunteers', order: 11, roleCode: 'volunteer' },
  { id: 'section-admins', type: 'admins', order: 12, roleCode: 'admin' },
  { id: 'section-reports', type: 'reports', order: 13, title: 'Rapports' },
  { id: 'section-vsc-reports', type: 'vscReports', order: 14, title: 'Rapports VSC' },
  { id: 'section-assessments', type: 'assessments', order: 15, title: 'Bilans' },
  { id: 'section-subjects', type: 'subjects', order: 16, title: 'Matières' },
  { id: 'section-levels', type: 'levels', order: 17, title: 'Niveaux' },
  { id: 'section-programs', type: 'programs', order: 18, title: 'Programmes' },
  { id: 'section-school-years', type: 'schoolYears', order: 19, title: 'Années scolaires' },
  { id: 'section-activity-types', type: 'activityTypes', order: 20, title: "Types d'activités" },
  { id: 'section-activities', type: 'activities', order: 21, title: 'Activités' },
  { id: 'section-holidays', type: 'holidays', order: 22, title: 'Vacances' },
  { id: 'section-partners', type: 'partners', order: 23, title: 'Partenaires' },
  { id: 'section-sport-clubs', type: 'sport-clubs', order: 24, title: 'Clubs de sport' },
  { id: 'section-parents', type: 'parents', order: 25, roleCode: 'parent' },
  { id: 'section-events', type: 'events', order: 26, title: 'Événements' },
];

/** Tailwind width class for each {@link ElementWidth}. */
export const DASHBOARD_WIDTH_CLASSES: Record<ElementWidth, string> = {
  '1/3': 'w-1/3',
  '1/2': 'w-1/2',
  '2/3': 'w-2/3',
  full: 'w-full',
  auto: 'w-auto',
};

/** Neighbor width when resizing a 2-up row. */
export const DASHBOARD_COMPLEMENTARY_WIDTH: Record<ElementWidth, ElementWidth> = {
  full: '1/3',
  '2/3': '1/3',
  '1/2': '1/2',
  '1/3': '2/3',
  auto: 'auto',
};

/** Drop-zone copy while a section is dragged. */
export const DASHBOARD_DROP_ZONE_LABELS = {
  dropHere: 'Déposer ici',
  bringBack: 'Ramener ici',
} as const;
